"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Sparkles, CircleCheck as CheckCircle2, Phone } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const features = ["Custom cabinet joinery & islands", "Quartz & natural stone fabrication", "Historic & modern spatial planning"];
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const transitionConfig = {
    duration: 0.4,
    ease: [0.16, 1, 0.3, 1]
  };
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: transitionConfig
    }
  };
  const staggerContainer = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  return <section id="hero" data-nav="light" className="relative overflow-hidden bg-background pt-28 pb-20 sm:pt-36 sm:pb-28 lg:pt-40 lg:pb-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          {/* Left Column: Copy and Conversion */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="flex flex-col lg:col-span-7">
            {/* Specialty Eyebrow Badge */}
            <motion.div variants={fadeUp} className="mb-5 flex items-center gap-2">
              <Badge variant="outline" className="rounded-full border-border bg-secondary/80 px-3.5 py-1 text-xs font-semibold tracking-wider text-foreground uppercase shadow-2xs">
                <Sparkles className="mr-1.5 size-3.5 text-primary" />
                Decatur Residential Craftsmanship
              </Badge>
            </motion.div>

            {/* Main Headline */}
            <motion.h1 variants={fadeUp} className="font-serif text-4xl font-semibold tracking-tight text-foreground sm:text-5xl lg:text-6xl leading-[1.1]">
              Kitchen remodeling in Decatur, Georgia
            </motion.h1>

            {/* Architectural Hairline Accent */}
            <motion.div variants={fadeUp} className="mt-6 h-[2px] w-24 bg-gradient-to-r from-primary via-border to-transparent" />

            {/* Supporting Copy */}
            <motion.p variants={fadeUp} className="mt-6 max-w-2xl text-base text-muted-foreground sm:text-lg leading-relaxed">
              Tailored kitchen design built around how you cook and gather. We blend custom cabinetry, premium quartz surfaces, and meticulous architectural detail to honor the distinct character of your Decatur home.
            </motion.p>

            {/* Highlights List */}
            <motion.ul variants={fadeUp} className="mt-6 flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:gap-6">
              {features.map((feature, i) => <li key={feature} data-index={i} className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>{feature}</span>
                </li>)}
            </motion.ul>

            {/* CTA Group */}
            <motion.div variants={fadeUp} className="mt-8 flex flex-wrap items-center gap-4 sm:gap-5">
              <Button asChild size="lg" className="h-12 rounded-lg bg-primary px-7 text-sm font-semibold text-primary-foreground shadow-sm transition-[color,background-color,border-color,box-shadow] hover:bg-primary/90">
                <Link to="#" className="inline-flex items-center gap-2">
                  <span>Request an estimate</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="h-12 rounded-lg border-border bg-card/80 px-6 text-sm font-semibold text-foreground backdrop-blur-sm shadow-xs transition-[color,background-color,border-color,box-shadow] hover:bg-secondary">
                <Link to="#">Explore project gallery</Link>
              </Button>
            </motion.div>

            {/* Direct Contact Teaser */}
            <motion.div variants={fadeUp} className="mt-8 flex items-center gap-3 text-xs text-muted-foreground border-t border-border pt-6">
              <div className="flex size-7 items-center justify-center rounded-full bg-secondary text-primary">
                <Phone className="size-3.5" />
              </div>
              <span>
                Prefer a quick conversation? Speak with our team directly at{" "}
                <a href="tel:+14708479076" className="font-medium text-foreground underline-offset-4 hover:underline">
                  (470) 847-9076
                </a>
              </span>
            </motion.div>
          </motion.div>

          {/* Right Column: Architectural Photography & Floating Spec Plate */}
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: [0.16, 1, 0.3, 1]
        }} className="relative lg:col-span-5">
            <div className="relative mx-auto max-w-lg lg:max-w-none">
              {/* Main Framed Hero Image */}
              <div className="overflow-hidden rounded-xl border border-border bg-muted shadow-md">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/decatur_kitchen_remodel_hero.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/decatur_kitchen_remodel_hero.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/decatur_kitchen_remodel_hero.webp-wvc-sizes" alt="Remodeled Decatur kitchen featuring natural wood cabinetry and quartz island" loading="eager" className="aspect-[4/3] w-full object-cover sm:aspect-[5/4] lg:aspect-[4/5]" />
              </div>

              {/* Dimensional Frosted Glass Detail Card */}
              <div className="mt-4 rounded-xl border border-border bg-card/90 p-4 sm:p-5 shadow-lg backdrop-blur-md sm:absolute sm:-bottom-6 sm:-left-6 sm:mt-0 sm:max-w-xs sm:rounded-lg">
                <p className="text-xs font-bold uppercase tracking-wider text-primary">
                  Custom Residential Craftsmanship
                </p>
                <p className="mt-1 text-sm font-semibold text-card-foreground">
                  Bespoke Kitchen Renovation
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Custom hardwood cabinetry, stone fabrication & thoughtful spatial flow
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}